import './index.css'

import React from 'react'
import type { DropdownOption } from 'types/ui'
type Props = {
  onChange: (...args: Array<any>) => any
  onFocus?: (...args: Array<any>) => any
  onBlur?: (...args: Array<any>) => any
  className?: string
  selectClassName?: string
  value: string
  name?: string
  options?: DropdownOption[]
  caption?: string
  multiple?: boolean
  disabled?: boolean
}

/**
 * A native HTML select dropdown component with customizable styling and options
 * @param name - The name attribute for the select element
 * @param className - CSS classes to apply to the wrapper div
 * @param selectClassName - CSS classes to apply to the select element (defaults to 'Form_Select')
 * @param value - The currently selected value
 * @param onChange - Callback fired when the selection changes
 * @param onFocus - Callback fired when the select receives focus
 * @param onBlur - Callback fired when the select loses focus
 * @param options - Array of dropdown options to display (defaults to empty array)
 * @param caption - Optional caption text shown as a disabled placeholder option
 * @param multiple - Whether to allow multiple selections (defaults to false)
 * @param disabled - Whether the dropdown is disabled (defaults to false)
 * @returns A native select element wrapped in a div with accessible title attribute
 */
// SLAM-1943: Defaults moved to params because React 19 ignores defaultProps on function components.
const Dropdown = ({
  name,
  className,
  selectClassName = 'Form_Select',
  value,
  onChange,
  onFocus,
  onBlur,
  options = [],
  caption,
  multiple,
  disabled,
}: Props): React.ReactElement<'div'> => {
  let title: string | undefined = undefined

  if (value) {
    const option = options.find(o => o.value === value)
    if (option) title = option.label
  }

  return (
    <div className={className} title={!disabled ? title : undefined}>
      <select
        className={selectClassName}
        value={value}
        onChange={onChange}
        name={name}
        multiple={multiple}
        disabled={disabled}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        {caption && (
          <option value="" disabled>
            {caption}
          </option>
        )}
        {options.map((option, index) => (
          <option value={option.value} key={index} disabled={option.blank}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  )
}

export default Dropdown
